import { HttpClient } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { environment } from '../../environments/environment';
import { BehaviorSubject, EMPTY, ReplaySubject, take, tap } from 'rxjs';
import { UserService } from '../user/user.service';
import { AuthService } from '../auth/auth.service';
import { Dialog } from '@angular/cdk/dialog';
import { SignInModalComponent } from '../../app/modals/sign-in-modal/sign-in-modal.component';
import { WorkOfArtService } from '../work-of-art/work-of-art.service';

@Injectable({
  providedIn: 'root',
})
export class ArtistService {
  private _backendUrl = environment.backendUrl;
  private _httpClient = inject(HttpClient);

  private _workOfArtService = inject(WorkOfArtService);

  dialog = inject(Dialog);

  private _artists: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  private _artist: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  private _configCallsPage: ReplaySubject<any> = new ReplaySubject<any>(1);

  private _worksOfArtArtist: BehaviorSubject<any> = new BehaviorSubject<any>(
    []
  );

  private _randomArtists: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  private _randomWorksOfArt: BehaviorSubject<any> = new BehaviorSubject<any>(
    []
  );
  constructor() {}

  get artists$() {
    return this._artists.asObservable();
  }
  get artist$() {
    return this._artist.asObservable();
  }

  set artists(artists: any) {
    this._artists.next(artists);
  }
  set artist(artist: any) {
    this._artist.next(artist);
  }

  get configCallsPage$() {
    return this._configCallsPage.asObservable();
  }
  set configCallsPage(configCallsPage: any) {
    this._configCallsPage.next(configCallsPage);
  }

  get worksOfArtArtist$() {
    return this._worksOfArtArtist.asObservable();
  }
  set worksOfArtArtist(worksOfArtArtist: any) {
    this._worksOfArtArtist.next(worksOfArtArtist);
  }

  get randomArtists$() {
    return this._randomArtists.asObservable();
  }
  set randomArtists(randomArtists: any) {
    this._randomArtists.next(randomArtists);
  }

  get randomWorksOfArt$() {
    return this._randomWorksOfArt.asObservable();
  }

  set randomWorksOfArt(randomWorksOfArt: any) {
    this._randomWorksOfArt.next(randomWorksOfArt);
  }

  private _injector = inject(Injector);
  private get authService(): AuthService {
    return this._injector.get(AuthService);
  }

  // getvalue

  getArtists(
    page: any,
    search: {
      name: string;
      category: string;
    }
  ) {
    return this._httpClient
      .get(`${this._backendUrl}client/artists`, {
        params: {
          page,
          ...search,
        },
      })
      .pipe(
        tap((response: any) => {
          if (response.payload.artists.length === 0) {
            this._artists.next([]);
            return;
          }
          const artistx = this._artists.getValue();

          this._artists.next([...artistx, ...response.payload.artists]);
        })
      );
  }
  getPagesArtists(search: any = {}) {
    return this._httpClient
      .get(`${this._backendUrl}client/artists/pages`, {
        params: {
          ...search,
        },
      })
      .pipe(
        tap((response: any) => {
          this._configCallsPage.next(response.payload);
        })
      );
  }
  getArtist(id: string, src?: string) {
    const options = src ? { params: { src } } : {};
    return this._httpClient
      .get(`${this._backendUrl}client/artists/${id}`, options)
      .pipe(
        tap((response: any) => {
          this._artist.next(response.payload.artist);
          this._worksOfArtArtist.next(response.payload.artworks);
          this._randomArtists.next(response.payload.randomArtists);
          this._randomWorksOfArt.next(response.payload.randomArtworks);
        })
      );
  }

  clearArtists() {
    this._artists.next([]);
  }

  postfollowing(idArtist: string) {
    // check if user is authenticated
    if (!this.authService.authenticated) {
      // If not authenticated, open the sign-in modal
      this.dialog.open(SignInModalComponent, { data: {} });
      return EMPTY;
    }
    // update artist in list
    const artists = this._artists.getValue();
    const updatedArtists = artists.map((artist: any) => {
      if (artist._id === idArtist) {
        return { ...artist, isFollowing: true };
      }
      return artist;
    });
    this._artists.next(updatedArtists);

    const artist = this._artist.getValue();
    if (artist && artist._id === idArtist) {
      this._artist.next({ ...artist, isFollowing: true });
    }

    const randomArtists = this._randomArtists.getValue();
    const updatedRandomArtists = randomArtists.map((artist: any) => {
      if (artist._id === idArtist) {
        return { ...artist, isFollowing: true };
      }
      return artist;
    });
    this._randomArtists.next(updatedRandomArtists);

    this._workOfArtService.addFollowingArtistToWorksOfArt(idArtist);

    // /client/artists/:id/follow
    return this._httpClient
      .post(`${this._backendUrl}client/artists/${idArtist}/follow`, {})
      .pipe(
        take(1),
        tap({
          next: (response: any) => {
            // Handle the response if needed
          },
          error: (error: any) => {
            console.error('Error following artist:', error);

            const artists = this._artists.getValue();
            const updatedArtists = artists.map((artist: any) => {
              if (artist._id === idArtist) {
                return { ...artist, isFollowing: false };
              }
              return artist;
            });
            this._artists.next(updatedArtists);
            this.dialog.open(SignInModalComponent, {
              data: {},
            });
          },
        })
      );
  }

  postunfollowing(idArtist: string) {
    // check if user is authenticated
    if (!this.authService.authenticated) {
      // If not authenticated, open the sign-in modal
      this.dialog.open(SignInModalComponent, { data: {} });
      return EMPTY;
    }

    const artists = this._artists.getValue();
    const updatedArtists = artists.map((artist: any) => {
      if (artist._id === idArtist) {
        return { ...artist, isFollowing: false };
      }
      return artist;
    });
    this._artists.next(updatedArtists);

    const artist = this._artist.getValue();
    if (artist && artist._id === idArtist) {
      this._artist.next({ ...artist, isFollowing: false });
    }

    const randomArtists = this._randomArtists.getValue();
    const updatedRandomArtists = randomArtists.map((artist: any) => {
      if (artist._id === idArtist) {
        return { ...artist, isFollowing: false };
      }
      return artist;
    });
    this._randomArtists.next(updatedRandomArtists);

    this._workOfArtService.removeFollowingArtistToWorksOfArt(idArtist);

    return this._httpClient
      .delete(`${this._backendUrl}client/artists/${idArtist}/follow`, {})
      .pipe(
        take(1),
        tap((response: any) => {
          // Handle the response if needed
        })
      );
  }

  clearFollowingArtists() {
    const artists = this._artists.getValue();
    const updatedArtists = artists.map((artist: any) => {
      return { ...artist, isFollowing: false };
    });
    this._artists.next(updatedArtists);

    const artist = this._artist.getValue();
    if (artist) {
      this._artist.next({ ...artist, isFollowing: false });
    }

    const randomArtists = this._randomArtists.getValue();
    const updatedRandomArtists = randomArtists.map((artist: any) => {
      return { ...artist, isFollowing: false };
    });
    this._randomArtists.next(updatedRandomArtists);
  }
}
